import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Build as BuildIcon,
  Delete as DeleteIcon,
  Inventory as InventoryIcon,
} from '@mui/icons-material';
import { Part, Tool, partsApi, toolsApi } from '../services/api';
import { stringOptionLabel } from '../utils/autocompleteOptions';

export interface WizardPartSelection {
  part_id: number;
  part_name?: string;
  part_number?: string;
  quantity: number;
  is_optional?: boolean;
  notes?: string;
}

export interface WizardToolSelection {
  tool_id: number;
  tool_name?: string;
  tool_number?: string;
  quantity: number;
  is_optional?: boolean;
  notes?: string;
}

function isSysCommPart(part: {
  part_id?: number;
  part_number?: string | null;
}): boolean {
  return (
    String(part.part_number || '').toUpperCase() === 'SYS-COMM' || part.part_id === 60
  );
}

interface WizardPartsToolsStepProps {
  parts: WizardPartSelection[];
  tools: WizardToolSelection[];
  onPartsChange: (parts: WizardPartSelection[]) => void;
  onToolsChange: (tools: WizardToolSelection[]) => void;
  /** When true (new set), auto-add SYS-COMM once the catalog loads. */
  seedSysComm?: boolean;
}

const WizardPartsToolsStep: React.FC<WizardPartsToolsStepProps> = ({
  parts,
  tools,
  onPartsChange,
  onToolsChange,
  seedSysComm = false,
}) => {
  const [availableParts, setAvailableParts] = useState<Part[]>([]);
  const [availableTools, setAvailableTools] = useState<Tool[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [partPick, setPartPick] = useState<Part | null>(null);
  const [toolPick, setToolPick] = useState<Tool | null>(null);
  const [sysCommSeeded, setSysCommSeeded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setLoadError(null);
        const [partsRes, toolsRes] = await Promise.all([
          // partsApi.getAll walks all pages (API defaults to 20/page).
          partsApi.getAll(),
          toolsApi.getAll(),
        ]);
        if (cancelled) return;
        setAvailableParts(partsRes.data.parts || []);
        setAvailableTools(toolsRes.data.tools || []);
      } catch (err: any) {
        if (!cancelled) {
          setLoadError(err?.response?.data?.error || 'Failed to load parts/tools catalog');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Reset seed gate when switching create ↔ edit or reopening wizard.
  useEffect(() => {
    setSysCommSeeded(false);
  }, [seedSysComm]);

  const resolvePartName = (part: Part) =>
    part.part_name || (part as { name?: string }).name || undefined;

  // Default part for every new MakerSet — platform commission.
  useEffect(() => {
    if (!seedSysComm || sysCommSeeded || loading || !availableParts.length) return;
    if (parts.some(isSysCommPart)) {
      setSysCommSeeded(true);
      return;
    }
    const commission = availableParts.find(isSysCommPart);
    if (!commission?.part_id) {
      setSysCommSeeded(true);
      return;
    }
    onPartsChange([
      {
        part_id: commission.part_id,
        part_name: resolvePartName(commission) || 'System Commission',
        part_number: commission.part_number || 'SYS-COMM',
        quantity: 1,
        is_optional: false,
        notes: 'System commission (required for all sets)',
      },
      ...parts,
    ]);
    setSysCommSeeded(true);
    // parts/onPartsChange intentionally omitted — seed once after catalog load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedSysComm, sysCommSeeded, loading, availableParts]);

  const addPart = (part: Part | null) => {
    if (!part?.part_id) return;
    if (parts.some((p) => p.part_id === part.part_id)) {
      setPartPick(null);
      return;
    }
    onPartsChange([
      ...parts,
      {
        part_id: part.part_id,
        part_name: resolvePartName(part),
        part_number: part.part_number,
        quantity: 1,
        is_optional: false,
        notes: '',
      },
    ]);
    setPartPick(null);
  };

  const addTool = (tool: Tool | null) => {
    if (!tool?.tool_id) return;
    if (tools.some((t) => t.tool_id === tool.tool_id)) {
      setToolPick(null);
      return;
    }
    onToolsChange([
      ...tools,
      {
        tool_id: tool.tool_id,
        tool_name: tool.tool_name,
        tool_number: tool.tool_number,
        quantity: 1,
        is_optional: false,
        notes: '',
      },
    ]);
    setToolPick(null);
  };

  const partOptions = availableParts.filter(
    (p) => !parts.some((selected) => selected.part_id === p.part_id)
  );
  const toolOptions = availableTools.filter(
    (t) => !tools.some((selected) => selected.tool_id === t.tool_id)
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <InventoryIcon color="primary" />
        Parts & Tools
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Choose the parts and tools this set uses first. The next step generates instructions and
        learning outcomes from these materials.
      </Typography>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        {seedSysComm
          ? 'SYS-COMM (system commission) is added by default for every new set. Add any other parts you need. Tools are optional but help the AI write safer, more accurate steps.'
          : 'Add at least one part before continuing. Tools are optional but help the AI write safer, more accurate steps.'}
      </Alert>

      <Typography variant="subtitle1" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <InventoryIcon fontSize="small" /> Parts
        <Chip size="small" label={parts.length} />
      </Typography>
      <Autocomplete
        sx={{ mb: 1 }}
        options={partOptions}
        loading={loading}
        value={partPick}
        onChange={(_, value) => addPart(value)}
        getOptionLabel={(option) =>
          `${resolvePartName(option) || 'Part'} (${option.part_number || option.part_id})`
        }
        isOptionEqualToValue={(a, b) => a.part_id === b.part_id}
        renderInput={(params) => (
          <TextField {...params} label="Add part" placeholder="Search parts…" />
        )}
      />
      <List dense>
        {parts.map((part, index) => {
          const locked = isSysCommPart(part);
          return (
            <ListItem
              key={part.part_id}
              secondaryAction={
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    size="small"
                    type="number"
                    label="Qty"
                    value={part.quantity}
                    onChange={(e) => {
                      const quantity = Math.max(1, parseInt(e.target.value, 10) || 1);
                      const next = [...parts];
                      next[index] = { ...part, quantity };
                      onPartsChange(next);
                    }}
                    sx={{ width: 88 }}
                    inputProps={{ min: 1 }}
                    disabled={locked}
                  />
                  <IconButton
                    edge="end"
                    aria-label={locked ? 'SYS-COMM required' : 'remove part'}
                    disabled={locked}
                    title={
                      locked
                        ? 'SYS-COMM is required for all MakerSets'
                        : 'Remove part'
                    }
                    onClick={() =>
                      onPartsChange(parts.filter((_, i) => i !== index))
                    }
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              }
            >
              <ListItemText
                primary={
                  locked
                    ? `${part.part_name || 'System Commission'} (required)`
                    : part.part_name || `Part #${part.part_id}`
                }
                secondary={part.part_number || undefined}
              />
            </ListItem>
          );
        })}
      </List>
      {parts.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
          No parts selected yet.
        </Typography>
      )}

      <Typography
        variant="subtitle1"
        sx={{ mt: 3, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <BuildIcon fontSize="small" /> Tools
        <Chip size="small" label={tools.length} />
      </Typography>
      <Autocomplete
        sx={{ mb: 1 }}
        options={toolOptions}
        loading={loading}
        value={toolPick}
        onChange={(_, value) => addTool(value)}
        getOptionLabel={(option) =>
          stringOptionLabel(
            `${option.tool_name || 'Tool'} (${option.tool_number || option.tool_id})`
          )
        }
        isOptionEqualToValue={(a, b) => a.tool_id === b.tool_id}
        renderInput={(params) => (
          <TextField {...params} label="Add tool" placeholder="Search tools…" />
        )}
      />
      <List dense>
        {tools.map((tool, index) => (
          <ListItem
            key={tool.tool_id}
            secondaryAction={
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  size="small"
                  type="number"
                  label="Qty"
                  value={tool.quantity}
                  onChange={(e) => {
                    const quantity = Math.max(1, parseInt(e.target.value, 10) || 1);
                    const next = [...tools];
                    next[index] = { ...tool, quantity };
                    onToolsChange(next);
                  }}
                  sx={{ width: 88 }}
                  inputProps={{ min: 1 }}
                />
                <IconButton
                  edge="end"
                  aria-label="remove tool"
                  onClick={() => onToolsChange(tools.filter((_, i) => i !== index))}
                >
                  <DeleteIcon />
                </IconButton>
              </Stack>
            }
          >
            <ListItemText
              primary={tool.tool_name || `Tool #${tool.tool_id}`}
              secondary={tool.tool_number || undefined}
            />
          </ListItem>
        ))}
      </List>
      {tools.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No tools selected yet (optional).
        </Typography>
      )}
    </Box>
  );
};

export default WizardPartsToolsStep;
